import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { RotateIcon } from '@rbilabs/components-library/build/components/rotate-icon';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import PaymentMethodOption, { IPaymentMethodOptionProps } from 'components/payment-method-option';
import { IPaymentMethod } from 'state/payment/types';
import { primitive } from 'styles/constants/primitives';
import { routes } from 'utils/routing';

import { AddNewCardLink } from './add-new-card-link';
import { PAYMENT_METHOD_OPTION_HEIGHT } from './constants';
import { HoverTheme, IHover } from './types';

export { HoverTheme } from './types';

interface IStyledPaymentMethodOption extends IPaymentMethodOptionProps {
  hoverTheme: HoverTheme;
}
interface IDisplayProps {
  open: boolean;
  children?: ReactNode[];
  hasAddPaymentMethod?: boolean;
}

interface IPaymentMethods {
  selectedMethod: IPaymentMethod | undefined;
  nonSelectedMethods: IPaymentMethod[];
  onOptionClick: (arg0: string) => void;
  onNavigateClick?: (arg0: MouseEvent, arg1: string) => void;
  hoverTheme?: HoverTheme;
}

interface IPaymentMethodsContainer {
  hoverTheme: HoverTheme;
}

const DisplayToggle = styled.div<IHover>`
  position: absolute;
  right: 0.875rem;
  top: 1.15rem;
  pointer-events: none;
  z-index: ${Styles.zIndex.normal};

  & a {
    display: none;
  }
`;

const StyledPaymentMethodOption = styled(PaymentMethodOption)<IStyledPaymentMethodOption>`
  height: ${PAYMENT_METHOD_OPTION_HEIGHT}rem;
  z-index: ${Styles.zIndex.below};

  & span {
    font-size: 12px;
    margin-top: 2px; // to make font appear vertically centred
    color: ${p =>
      p.hoverTheme === HoverTheme.DARK ? primitive.th.$coffeeBrown : Styles.color.grey.two};
  }

  &:hover {
    background-color: ${p => (p.hoverTheme === HoverTheme.DARK ? '#A5031D' : Styles.color.primary)};
    color: ${p => (p.hoverTheme === HoverTheme.DARK ? 'inherit' : Styles.color.white)};

    & span {
      color: ${Styles.color.white};
    }
  }
`;

export const PaymentMethodsContainer = styled.div<IPaymentMethodsContainer>`
  position: relative;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  margin-bottom: 1.5rem;
  overflow: hidden;

  & ${StyledPaymentMethodOption} {
    border-bottom: 1px solid
      ${p => (p.hoverTheme === HoverTheme.DARK ? 'rgba(0, 0, 0, 0.1)' : Styles.color.grey.four)};
  }
`;

const PaymentMethods = styled.div`
  svg {
    fill: ${primitive.th.$coffeeBrown};
  }
  &:hover svg {
    fill: ${Styles.color.white};
  }
`;

const HiddenPaymentMethods = styled.div<IDisplayProps>`
  height: ${p =>
    p.open
      ? (p.hasAddPaymentMethod
          ? React.Children.count(p.children)
          : React.Children.count(p.children) - 1) * PAYMENT_METHOD_OPTION_HEIGHT
      : 0}rem;
  overflow: hidden;
  transition: height 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const PaymentMethodSelect: React.FC<IPaymentMethods> = ({
  selectedMethod,
  nonSelectedMethods,
  onOptionClick,
  onNavigateClick,
  hoverTheme = HoverTheme.DARK,
}) => {
  const { formatMessage } = useIntl();
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);

  const handleOptionClick = useCallback(
    (fdAccountId: string) => {
      onOptionClick(fdAccountId);
      // Delay closing payment methods UI
      setTimeout(() => setShowPaymentMethods(false), 200);
    },
    [onOptionClick]
  );

  const handleTogglePaymentMethods = useCallback(() => {
    if (nonSelectedMethods.length > 0 || onNavigateClick) {
      setShowPaymentMethods(currentShowPaymentMethods => !currentShowPaymentMethods);
    }
  }, [setShowPaymentMethods, nonSelectedMethods.length, onNavigateClick]);

  const handleNavigateClick = useCallback(
    e => {
      if (onNavigateClick) {
        onNavigateClick(e, routes.payment);
      }
    },
    [onNavigateClick]
  );

  useEffect(() => {
    setShowPaymentMethods(!selectedMethod);
  }, [selectedMethod]);

  // Show the arrow if the user has more that one payment method
  // or if the user has a selected payment method and the dropdown has a link to add a new CC
  const showArrow = !!nonSelectedMethods.length || (!!selectedMethod && !!onNavigateClick);

  return (
    <PaymentMethodsContainer data-testid="payment-methods-container" hoverTheme={hoverTheme}>
      {selectedMethod && (
        <PaymentMethods>
          {showArrow && (
            <DisplayToggle
              data-testid="payment-options-display-toggle"
              hoverTheme={hoverTheme}
              onClick={handleTogglePaymentMethods}
              aria-expanded={showPaymentMethods}
              aria-controls="payment-methods"
            >
              <RotateIcon active={showPaymentMethods}>
                <Icon
                  icon="select"
                  color={hoverTheme === HoverTheme.DARK ? 'icon-reversed' : 'icon-default'}
                  width="24px"
                  aria-hidden
                />
              </RotateIcon>
            </DisplayToggle>
          )}
          <StyledPaymentMethodOption
            key={selectedMethod.fdAccountId}
            method={selectedMethod}
            selected
            onClick={handleTogglePaymentMethods}
            showDefault={false}
            hoverTheme={hoverTheme}
            disableHover
            hideBorder={!showPaymentMethods}
          />
        </PaymentMethods>
      )}
      <HiddenPaymentMethods
        id="payment-methods"
        open={showPaymentMethods}
        hasAddPaymentMethod={!!onNavigateClick}
      >
        {nonSelectedMethods.map((method: IPaymentMethod) => {
          const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
          return (
            <StyledPaymentMethodOption
              key={accountIdentifier}
              method={method}
              onClick={() => handleOptionClick(accountIdentifier)}
              showDefault={false}
              hoverTheme={hoverTheme}
              disableHover
            />
          );
        })}
        {onNavigateClick && (
          <AddNewCardLink hoverTheme={hoverTheme} onClick={handleNavigateClick}>
            {formatMessage({ id: 'addNewPaymentMethod' })}
          </AddNewCardLink>
        )}
      </HiddenPaymentMethods>
    </PaymentMethodsContainer>
  );
};

export default PaymentMethodSelect;
